import React, {useCallback, useEffect, useRef, useState} from "react";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import PeriodInput from "../../../components/input/period-input.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import ShowHideColumnsGrid from "../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {FaEraser, FaFileExcel, FaPaste, FaPlus, FaQuestion, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import useHasPermission from "../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../utils/enums/permission-enum.js";
import projectUtils from "../../../utils/project-utils.js";
import GarrisonService from "../../../services/dispatch/garrison-service.js";
import DispatchListOptionsService from "../../../services/dispatch/dispatch-list-options-service.js";
import {
    exportGridData,
    sanitizeFilterValue,
    selectedValue,
    validateDateInterval,
} from "../utils/dispatch-list-utils.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const initialFilters = {
    dtStart: "",
    dtEnd: "",
    contingent: null,
    garrison: "",
    containedText: "",
};

const buildFilter = (values) => [
    {field: "garrison_start", type: "keywords", value: {dtStart: values.dtStart || "", dtEnd: values.dtEnd || ""}},
    {field: "contingent", type: "like", value: selectedValue(values.contingent)},
    {field: "garrison_name", type: "like", value: sanitizeFilterValue(values.garrison)},
    {field: "containedText", type: "like", value: sanitizeFilterValue(values.containedText)},
];

const exportRow = (row) => ({
    "ID": row.garrison_id,
    "Data/Hora inicial": row.garrison_start,
    "Data/Hora final": row.garrison_end,
    "Nome": row.garrison_name,
    "Equipe": row.garrison_team,
    "Função": row.garrison_intendedfunction,
    "Setor": row.garrison_sector,
    "Status": row.garrison_status,
    "Encerrada": row.garrison_finalized,
});

const HIDDEN_COLUMNS_STORAGE = "garrison-hidden-columns";
const DEFAULT_HIDDEN_COLUMNS = ["garrison_id"];

const GarrisonList = () => {
    const hasPermission = useHasPermission();
    const navigate = navigateHook();
    const gridRef = useRef(null);
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const canManageGarrison = hasPermission(PermissionEnum.GARRISON_WEB);
    const canManageMyGarrison = hasPermission(PermissionEnum.MY_GARRISON) || hasPermission(PermissionEnum.MYGARRISON);
    const allowOnlyMyGarrison = !canManageGarrison && canManageMyGarrison;

    const {data = [], isLoading} = useQuery({
        queryFn: () => GarrisonService.list({filter: [...filter]}),
        queryKey: ["dispatch-garrison", {filter}],
        placeholderData: keepPreviousData,
    });

    const {data: contingentOptions = [], isLoading: isLoadingContingents} = useQuery({
        queryFn: () => DispatchListOptionsService.contingents(),
        queryKey: ["dispatch-garrison-contingents"],
    });

    useEffect(() => {
        if (!allowOnlyMyGarrison || filterValues.contingent || !contingentOptions.length) return;
        setFilterValues(prev => ({...prev, contingent: contingentOptions[0]}));
    }, [allowOnlyMyGarrison, contingentOptions, filterValues.contingent]);

    const [columnDefs, setColumnDefs] = useState([]);

    useEffect(() => {
        const columns = [
            {field: "garrison_id", headerName: "ID", sort: "desc", width: 90, cellDataType: "number"},
            {field: "garrison_start", headerName: "Data/Hora inicial", width: 130, comparator: aggridUtils.agGridCompareDateTime},
            {field: "garrison_end", headerName: "Data/Hora final", width: 130, comparator: aggridUtils.agGridCompareDateTime},
            {field: "garrison_name", headerName: "Nome", minWidth: 240, flex: 1},
            {field: "garrison_team", headerName: "Equipe"},
            {field: "garrison_intendedfunction", headerName: "Função"},
            {field: "garrison_sector", headerName: "Setor"},
            {field: "garrison_status", headerName: "Status", width: 110},
            {field: "garrison_finalized", headerName: "Encerrada", width: 90},
            {
                field: "allowcopy",
                headerName: "Copiar",
                width: 65,
                sortable: false,
                cellRenderer: ({data: row}) => {
                    if (row?.allowcopy !== true && row?.allowcopy !== "true") return "";
                    return (
                        <div className={"w-full h-full flex justify-center items-center"}>
                            <Button
                                title="Copiar guarnição"
                                className="h-6.5! w-6.5! p-0!"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    navigate(`${API_DESPACHO_WEB}/garrison/form?cp=${row.garrison_id}`, true);
                                }}
                            >
                                <FaPaste/>
                            </Button>
                        </div>
                    );
                }
            },
        ]

        const hiddenColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE)) ?? DEFAULT_HIDDEN_COLUMNS;
        if (hiddenColumns) columns.forEach(c => hiddenColumns.includes(c.field) ? c.hide = true : "");

        setColumnDefs(columns);
    }, []);

    const toggleColumn = (field) => {
        setColumnDefs(prev => prev.map(column => column.field === field ? {...column, hide: !column.hide} : column));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    };

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const apply = useCallback(() => {
        const message = validateDateInterval(filterValues.dtStart, filterValues.dtEnd);
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        setFilter(buildFilter(filterValues));
    }, [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(prev => ({
            ...initialFilters,
            contingent: allowOnlyMyGarrison ? prev.contingent : null,
        }));
        setFilter([]);
    }, [allowOnlyMyGarrison]);

    const onRowClicked = ({data: row, event}) => {
        if (event?.target?.closest("button")) return;
        if (!row?.garrison_id) return;
        navigate(`${API_DESPACHO_WEB}/garrison/form/${row.garrison_id}`, true);
    };

    const openForm = () => navigate(API_DESPACHO_WEB + "/garrison/form", false);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    return (
        <Template isLoading={isLoading || isLoadingContingents}>
            <Template.Header>
                <Template.Title>Guarnições</Template.Title>
                <div className={"flex gap-2"}>
                    <Button target="success" onClick={() => exportGridData(gridRef.current?.api, exportRow)} title="Exportar">
                        <FaFileExcel/> Exportar
                    </Button>
                    {(canManageGarrison || canManageMyGarrison) &&
                        <Button onClick={openForm}><FaPlus/>Adicionar guarnição</Button>
                    }
                </div>
            </Template.Header>
            <Template.Body className="flex flex-col h-full min-h-0 space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow className="items-start">
                            <PeriodInput
                                startDateId="dtStart"
                                endDateId="dtEnd"
                                label="Início entre"
                                dateFormat="LL"
                                returnDateObj={false}
                                startDate={filterValues.dtStart}
                                endDate={filterValues.dtEnd}
                                setFilter={setFilterValues}
                                cols={6}
                            />
                            <SelectComponent
                                classNameWrapper="col-span-3"
                                id="contingent"
                                label="Contingente"
                                options={contingentOptions}
                                value={filterValues.contingent}
                                isDisabled={allowOnlyMyGarrison}
                                onChange={(option) => setFilterValues(prev => ({...prev, contingent: option}))}
                            />
                            <InputForm classNameWrapper="col-span-3" id="garrison" label="Guarnição" value={filterValues.garrison} onChange={handleInput}/>
                            <div className={"col-span-12 flex gap-4"}>
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id="containedText"
                                    label="Texto contido"
                                    inputClass="pr-9"
                                    title="Filtros: Observação inicial e observação final"
                                    value={filterValues.containedText}
                                    onChange={handleInput}
                                    span={
                                        <span
                                            className="absolute right-2 top-1/2 z-20 flex size-5 -translate-y-1/2 items-center justify-center rounded-full bg-[#E9ECEF] text-[#676a6c]"
                                            title="Filtros: Observação inicial e observação final"
                                            aria-label="Campos pesquisados pelo filtro Texto contido"
                                        >
                                            <FaQuestion size={10}/>
                                        </span>
                                    }
                                />
                                <div className="flex gap-2 justify-end">
                                    <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                                    <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-197px)] aggrid-row-pointer">
                            <AgGridReact
                                ref={gridRef}
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                onColumnVisible={onColumnVisible}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default GarrisonList;
